/**
 * RoutinePlayer — Full-screen orchestrator for daily routines.
 *
 * State machine: idle → exercise → rest → exercise → rest → ... → summary
 *
 * For each exercise:
 *   1. Calls tool-engine /execute to open a KTS session
 *   2. Mounts KtsMount with the session params
 *   3. On finish, seals the session and records the result
 *   4. Shows rest timer before next exercise
 *   5. After last exercise, shows RoutineResults summary
 */

import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth-context';
import KtsMount from '@/components/tools/KtsMount';
import RestTimer from './RestTimer';
import RoutineResults from './RoutineResults';

const TOOL_ENGINE = import.meta.env.VITE_TOOL_ENGINE_URL || '';
const KTS_URL = import.meta.env.VITE_KTS_URL || '';

interface RoutineStep {
  step_order: number;
  tool_id: string;
  rest_seconds: number;
  target_reps: number | null;
  exercise_name: string;
  heuristic_rule?: any;
}

interface RoutinePlayerProps {
  runId: string;
  routineName: string;
  steps: RoutineStep[];
  onClose: () => void;
}

interface StepResult {
  step_order: number;
  exercise_name: string;
  reps: number;
  kinetic_session_id: string | null;
}

type Phase = 'exercise' | 'rest' | 'summary';

export default function RoutinePlayer({ runId, routineName, steps, onClose }: RoutinePlayerProps) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('exercise');
  const [results, setResults] = useState<StepResult[]>([]);
  const [ktsSession, setKtsSession] = useState<{ session_id: string; ws_url: string } | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const startTimeRef = useRef(Date.now());

  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex >= steps.length - 1;

  // Open a KTS session for the current exercise
  const openSession = useCallback(async (toolId: string) => {
    setLoadingSession(true);
    setError(null);
    try {
      const res = await fetch(`${TOOL_ENGINE}/api/tools/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ toolId, inputs: {} }),
      });

      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error?.message || `Failed to start session: ${res.status}`);
      }

      if (json.data?.kts_session) {
        setKtsSession({
          session_id: json.data.kts_session.session_id,
          ws_url: json.data.kts_session.ws_url,
        });
      } else {
        throw new Error('No KTS session returned from tool engine');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingSession(false);
    }
  }, [token]);

  // Start first exercise on mount
  useState(() => {
    if (currentStep) {
      openSession(currentStep.tool_id);
    }
  });

  // Handle exercise completion
  const handleExerciseComplete = useCallback(async (sealResult: any) => {
    const step = steps[currentStepIndex];

    // Record the result
    const result: StepResult = {
      step_order: step.step_order,
      exercise_name: step.exercise_name,
      reps: sealResult.verified_reps ?? 0,
      kinetic_session_id: ktsSession?.session_id || null,
    };

    setResults((prev) => [...prev, result]);
    setKtsSession(null);

    // Notify backend of step completion
    try {
      await fetch(`${KTS_URL}/api/routines/runs/${runId}/step-complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          step_order: step.step_order,
          kinetic_session_id: ktsSession?.session_id,
          verified_reps: sealResult.verified_reps ?? 0,
        }),
      });
    } catch (err) {
      console.warn('[RoutinePlayer] step-complete failed (non-fatal)', err);
    }

    // Move to rest or summary
    if (isLastStep) {
      setPhase('summary');
      // Finish the routine
      try {
        await fetch(`${KTS_URL}/api/routines/runs/${runId}/finish`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (err) {
        console.warn('[RoutinePlayer] finish failed (non-fatal)', err);
      }
    } else if (step.rest_seconds > 0) {
      setPhase('rest');
    } else {
      // No rest — go straight to next exercise
      advanceToNextExercise();
    }
  }, [currentStepIndex, steps, isLastStep, ktsSession, runId, token]);

  // Advance to next exercise
  const advanceToNextExercise = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    setCurrentStepIndex(nextIndex);
    setPhase('exercise');

    if (steps[nextIndex]) {
      openSession(steps[nextIndex].tool_id);
    }
  }, [currentStepIndex, steps, openSession]);

  // Summary data
  const totalReps = results.reduce((sum, r) => sum + r.reps, 0);
  const totalTime = Math.round((Date.now() - startTimeRef.current) / 1000);

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl bg-red-50 p-8 text-center">
        <p className="text-lg font-semibold text-red-600">Error</p>
        <p className="mt-2 text-sm text-red-500">{error}</p>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white"
        >
          Back to Routines
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      {phase !== 'summary' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-gray-900">{routineName}</span>
            <span className="text-gray-500">
              Exercise {currentStepIndex + 1} of {steps.length}
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-500"
              style={{ width: `${((currentStepIndex + (phase === 'rest' ? 1 : 0)) / steps.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Exercise phase */}
      {phase === 'exercise' && (
        <div>
          {/* Exercise header */}
          <div className="mb-3 text-center">
            <h2 className="text-lg font-bold text-gray-900">{currentStep?.exercise_name}</h2>
            {currentStep?.target_reps && (
              <p className="text-sm text-blue-600">Target: {currentStep.target_reps} reps</p>
            )}
          </div>

          {/* Loading session */}
          {loadingSession && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
              <p className="mt-3 text-sm text-gray-500">Starting {currentStep?.exercise_name}...</p>
            </div>
          )}

          {/* KtsMount */}
          {!loadingSession && ktsSession && (
            <KtsMount
              sessionId={ktsSession.session_id}
              wsUrl={ktsSession.ws_url}
              token={token}
              heuristicRule={currentStep?.heuristic_rule}
              onComplete={handleExerciseComplete}
            />
          )}
        </div>
      )}

      {/* Rest phase */}
      {phase === 'rest' && currentStep && (
        <RestTimer
          seconds={currentStep.rest_seconds}
          nextExercise={steps[currentStepIndex + 1]?.exercise_name || 'Next'}
          targetReps={steps[currentStepIndex + 1]?.target_reps}
          onComplete={advanceToNextExercise}
          onSkip={advanceToNextExercise}
        />
      )}

      {/* Summary phase */}
      {phase === 'summary' && (
        <RoutineResults
          routineName={routineName}
          exercises={results.map((r) => ({ exercise: r.exercise_name, reps: r.reps }))}
          totalReps={totalReps}
          totalTimeSeconds={totalTime}
          onRunAgain={() => window.location.reload()}
          onClose={onClose}
        />
      )}
    </div>
  );
}
